import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Collapse,
  Alert,
} from '@mui/material';
import useSettings from '../hooks/useSettings.ts';
import { OrderPaymentType, OrderScheduleItem, OrderScheduleResponse, RequiredEventForm, EventFormInfo } from '../types';
import { SESSION_KEY_ORDER_UUID } from '../constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatCurrencyDisplay } from '../utils/index.tsx';
import { parseFormSchema, getFormRowKey } from '../utils/formUtils';
import type { CompletedSaleSnapshot } from '../types';
import { getOrderDetails } from '../hooks/cart/getOrderDetails';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import NextPlanIcon from '@mui/icons-material/NextPlan';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { eventsRequest } from '../utils/eventsRequest';
import getUserOutstandingForms, { mapOutstandingFormItemToRequiredEventForm } from '../hooks/user/getUserOutstandingForms';
import getEventByUuid from '../hooks/events/getEventByUuid';
import submitEventForm from '../hooks/user/submitEventForm';
import { Form, FormioProvider } from '@formio/react';
import { Formio } from '@formio/js';

dayjs.extend(utc);
dayjs.extend(timezone);

const ThankYouPage: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [saleSnapshot, setSaleSnapshot] = useState<CompletedSaleSnapshot | null>(null);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [orderLoading, setOrderLoading] = useState<boolean>(true);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState<boolean>(false);
  const [orderSchedules, setOrderSchedules] = useState<OrderScheduleItem[]>([]);
  
  // Forms state (hasOutstandingForms comes from getUserOutstandingForms)
  const [showForms, setShowForms] = useState<boolean>(false);
  const [hasOutstandingForms, setHasOutstandingForms] = useState<boolean>(false);
  const [outstandingForms, setOutstandingForms] = useState<RequiredEventForm[]>([]);
  const [formsLoading, setFormsLoading] = useState<boolean>(false);
  const [formsError, setFormsError] = useState<string | null>(null);
  const [expandedFormRowKeys, setExpandedFormRowKeys] = useState<Set<string>>(new Set());
  const [loadingFormId, setLoadingFormId] = useState<number | null>(null);
  const [formSchemas, setFormSchemas] = useState<Map<number, EventFormInfo>>(new Map());
  const [submitSuccess, setSubmitSuccess] = useState<Map<string, boolean>>(new Map());
  const [submittingFormRowKey, setSubmittingFormRowKey] = useState<string | null>(null);
  const formRefs = useRef<Map<string, { submit: () => void }>>(new Map());

  // Processing fee + totals are computed server-side and returned with the order snapshot.

  const {
    loading,
    showDescription,
    description,
    smallImage,
    mediumImage,
    largeImage,
  } = useSettings();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadOrderDetails = useCallback(async () => {
    setLoadError(false);
    setOrderLoading(true);
    const orderUuid = searchParams.get('order') ?? localStorage.getItem(SESSION_KEY_ORDER_UUID);
    if (!orderUuid) {
      setSaleSnapshot(null);
      setLoadError(true);
      setOrderLoading(false);
      return;
    }
    const { success, snapshot } = await getOrderDetails(orderUuid);
    if (success && snapshot) {
      setSaleSnapshot(snapshot);
    } else {
      setSaleSnapshot(null);
      setLoadError(true);
    }
    setOrderLoading(false);
  }, [searchParams]);

  useEffect(() => {
    loadOrderDetails();
  }, [loadOrderDetails]);

  const imageSelector = async () => {
    if (largeImage && window.innerWidth > 1080) {
      setSelectedImage(largeImage);
      return;
    }
    if (mediumImage && window.innerWidth > 450) {
      setSelectedImage(mediumImage);
      return;
    }
    setSelectedImage(smallImage);
  };

  useEffect(() => {
    imageSelector();
  }, [largeImage, mediumImage, smallImage]);

  const buildScheduleKey = useCallback((eventName?: string, childName?: string) => {
    return `${(eventName ?? '').trim().toLowerCase()}|${(childName ?? '').trim().toLowerCase()}`;
  }, []);

  const isScheduleItemValid = useCallback((item?: OrderScheduleItem | null): item is OrderScheduleItem => {
    if (!item || !item.timezone || !Array.isArray(item.occurrences) || item.occurrences.length === 0) return false;

    return item.occurrences.every((occ) => {
      if (!occ?.date || !occ?.startTime || !occ?.endTime) return false;
      const start = dayjs.tz(`${occ.date}T${occ.startTime}`, item.timezone);
      const end = dayjs.tz(`${occ.date}T${occ.endTime}`, item.timezone);
      return start.isValid() && end.isValid() && end.isAfter(start);
    });
  }, []);

  const scheduleMap = useMemo(() => {
    const map = new Map<string, OrderScheduleItem>();
    orderSchedules.forEach((item) => {
      map.set(buildScheduleKey(item.eventName, item.childName), item);
    });
    return map;
  }, [orderSchedules, buildScheduleKey]);

  const getScheduleForRegistration = useCallback(
    (reg: CompletedSaleSnapshot['registrations'][number]) => {
      return scheduleMap.get(buildScheduleKey(reg.eventName, reg.childName)) ?? null;
    },
    [scheduleMap, buildScheduleKey]
  );

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!saleSnapshot) return;
      // Use the same source-of-truth as loadOrderDetails(): query param first,
      // then localStorage (for cases where we came from Checkout).
      const orderUuid = searchParams.get('order') ?? localStorage.getItem(SESSION_KEY_ORDER_UUID);
      if (!orderUuid) {
        setScheduleError('Missing order information for calendar export.');
        return;
      }
      setScheduleLoading(true);
      setScheduleError(null);
      try {
        const response = await eventsRequest<OrderScheduleResponse>(`/orders/${orderUuid}/schedule`, 'get');
        const data = response?.data?.data ?? [];
        setOrderSchedules(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load order schedule', err);
        setOrderSchedules([]);
        setScheduleError('Unable to load schedule for Add to Calendar.');
      } finally {
        setScheduleLoading(false);
      }
    };

    fetchSchedule();
  }, [saleSnapshot, searchParams]);

  const fetchOutstandingForms = useCallback(async () => {
    setFormsLoading(true);
    setFormsError(null);
    try {
      const response = await getUserOutstandingForms();
      if (response?.isSuccess && response.data) {
        setHasOutstandingForms(response.data.hasOutstandingForms ?? false);
        const items = response.data.outstandingForms;
        setOutstandingForms(
          Array.isArray(items) ? items.map(mapOutstandingFormItemToRequiredEventForm) : []
        );
      } else {
        setHasOutstandingForms(false);
        setFormsError('Unable to load forms.');
      }
    } catch (err) {
      console.error('Failed to fetch outstanding forms', err);
      setHasOutstandingForms(false);
      setFormsError('Unable to load forms. Please try again.');
    } finally {
      setFormsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (saleSnapshot) {
      fetchOutstandingForms();
    }
  }, [saleSnapshot, fetchOutstandingForms]);

  const handleCompleteFormsClick = () => {
    if (!showForms) {
      fetchOutstandingForms();
    }
    setShowForms(!showForms);
  };

  const handleFormExpand = async (form: RequiredEventForm) => {
    const rowKey = getFormRowKey(form);
    const isCurrentlyExpanded = expandedFormRowKeys.has(rowKey);
    if (isCurrentlyExpanded) {
      setExpandedFormRowKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
      return;
    }

    setExpandedFormRowKeys((prev) => new Set(prev).add(rowKey));

    // If we already have the schema, don't fetch again
    if (formSchemas.has(form.id)) {
      return;
    }

    // If form has formUrl, open in new tab instead
    if (form.formUrl) {
      window.open(form.formUrl, '_blank', 'noopener,noreferrer');
      setExpandedFormRowKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
      return;
    }

    // Use inline form schema when API returned it
    if (form.form) {
      const schema = parseFormSchema(form.form);
      if (schema) {
        setFormSchemas(
          new Map(formSchemas.set(form.id, { id: form.id, name: form.formName, formSchema: form.form }))
        );
        return;
      }
    }

    // Fetch event details to get form schema when eventUuid is available
    if (!form.eventUuid) {
      setFormsError('Form schema not available.');
      return;
    }

    setLoadingFormId(form.id);
    try {
      const event = await getEventByUuid(form.eventUuid);
      if (event?.eventForm) {
        setFormSchemas(new Map(formSchemas.set(form.id, event.eventForm)));
      } else {
        setFormsError('Form schema not available for this event.');
      }
    } catch (err) {
      console.error('Failed to load form schema', err);
      setFormsError('Unable to load form. Please try again.');
    } finally {
      setLoadingFormId(null);
    }
  };

  const handleFormSubmit = async (form: RequiredEventForm, submission: unknown) => {
    const rowKey = getFormRowKey(form);
    setSubmittingFormRowKey(rowKey);
    try {
      const submittedForChilds = form.childId != null ? [form.childId] : [];
      const result = await submitEventForm(
        form.id,
        submission as { data: Record<string, unknown> },
        form.eventUuid,
        submittedForChilds,
        form.eventId
      );
      if (result.success) {
        setSubmitSuccess((prev) => new Map(prev).set(rowKey, true));
        setExpandedFormRowKeys((prev) => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
        // Refresh the forms list to remove completed form
        await fetchOutstandingForms();
      } else {
        setFormsError(result.message ?? 'Submit failed. Please try again.');
      }
    } catch (err) {
      console.error('Failed to submit form', err);
      setFormsError('Unable to submit form. Please try again.');
    } finally {
      setSubmittingFormRowKey(null);
    }
  };

  const handleAddToCalendar = (reg: CompletedSaleSnapshot['registrations'][number]) => {
    const schedule = getScheduleForRegistration(reg);
    if (!isScheduleItemValid(schedule)) {
      setScheduleError('Schedule data unavailable for calendar export.');
      return;
    }

    const sanitizedTitle = reg.eventName + (reg.childName ? ` - ${reg.childName}` : '');
    const escapeICS = (text: string) =>
      text.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');

    const lines: string[] = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Kindertales//Events//EN'];
    let addedEvents = 0;
    schedule.occurrences.forEach((occ, idx) => {
      const start = dayjs.tz(`${occ.date}T${occ.startTime}`, schedule.timezone);
      const end = dayjs.tz(`${occ.date}T${occ.endTime}`, schedule.timezone);
      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return;
      addedEvents += 1;
      lines.push(
        'BEGIN:VEVENT',
        `UID:${(schedule.eventUuid ?? 'order-schedule')}-${idx}-${Date.now()}`,
        `DTSTAMP:${dayjs().utc().format('YYYYMMDDTHHmmss')}Z`,
        `DTSTART;TZID=${schedule.timezone}:${start.format('YYYYMMDDTHHmmss')}`,
        `DTEND;TZID=${schedule.timezone}:${end.format('YYYYMMDDTHHmmss')}`,
        `SUMMARY:${escapeICS(sanitizedTitle)}`,
        `DESCRIPTION:${escapeICS('Event from Kindertales')}`,
        'END:VEVENT'
      );
    });

    if (addedEvents === 0) {
      setScheduleError('Schedule data unavailable for calendar export.');
      return;
    }

    lines.push('END:VCALENDAR');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reg.eventName?.replace(/\s+/g, '_') || 'event'}-schedule.ics`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading || orderLoading) {
    return (
      <div className="event-page p-5 mb-20">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (loadError || !saleSnapshot) {
    return (
      <div className="event-page p-5 mb-20">
        <Box
          sx={{
            pt: 4,
            pb: 6,
            maxWidth: 560,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            We couldn't load your purchase summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your purchase was successful. You can review your registrations in Family Profiles.
          </Typography>
          <Button
            variant="contained"
            onClick={() => loadOrderDetails()}
            sx={{ textTransform: 'none' }}
          >
            Try again
          </Button>
          <Box sx={{ mt: 2 }}>
            <Button
              onClick={() => navigate('/profile')}
              sx={{ textTransform: 'none' }}
            >
              View my registrations
            </Button>
          </Box>
        </Box>
      </div>
    );
  }

  const s = saleSnapshot;
  const processingFee = s.processingFee ?? 0;
  const processingFeeName = s.processingFeeName ?? '';
  const couponDiscount = s.couponDiscountTotal ?? 0;
  const totalPaidTodayWithFee = s.totalPaidToday + processingFee;

  const getRegistrationRemainingBalance = (
    reg: CompletedSaleSnapshot['registrations'][number]
  ): number => {
    if (reg.remainingForItem != null && reg.remainingForItem !== '') {
      return Number(reg.remainingForItem);
    }
    const itemPrice = Number(reg.itemPrice ?? reg.amountPaid ?? 0);
    const paidToday = Number(reg.paidToday ?? reg.amountPaid ?? 0);
    return Math.max(0, itemPrice - paidToday);
  };

  return (
    <div className="event-page p-5 mb-20">
      <Box
        sx={{
          position: 'relative',
          pt: 4,
          pb: 6,
          maxWidth: 640,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {selectedImage && (
          <img style={{ maxHeight: 200, marginBottom: 8, display: 'none' }} src={selectedImage} alt="Banner" />
        )}
        <Typography fontSize={28} fontWeight={700} textAlign="center" color="#4E4E4E">
          {showDescription && (description || 'Thank you for your purchase')}
        </Typography>

        <Box width="100%" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Payment Summary card */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventNoteIcon fontSize="small" /> Payment summary
              </Typography>

              <>
                {s.totalBeforeCoupon != null && s.totalBeforeCoupon > 0 && (
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span style={{ color: '#4E4E4E' }}>Subtotal</span>
                    <strong>${formatCurrencyDisplay(s.totalBeforeCoupon)}</strong>
                  </Typography>
                )}
                {couponDiscount > 0 && (
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span style={{ color: '#4E4E4E' }}>Coupon discount</span>
                    <strong>-${formatCurrencyDisplay(couponDiscount)}</strong>
                  </Typography>
                )}
                {s.payInFullDiscount != null && s.payInFullDiscount > 0 && (
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span style={{ color: '#4E4E4E' }}>Paid in full discount</span>
                    <strong>-${formatCurrencyDisplay(s.payInFullDiscount)}</strong>
                  </Typography>
                )}
                {s.orderType === OrderPaymentType.DEPOSIT && s.depositPaid != null && (
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span style={{ color: '#4E4E4E' }}>Deposit</span>
                    <strong>${formatCurrencyDisplay(s.depositPaid)}</strong>
                  </Typography>
                )}
                {processingFee > 0 && (
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span style={{ color: '#4E4E4E' }}>
                      Processing fee
                      {processingFeeName ? ` (${processingFeeName})` : ''}
                    </span>
                    <strong>${formatCurrencyDisplay(processingFee)}</strong>
                  </Typography>
                )}
                <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <span style={{ color: '#4E4E4E' }}>Total paid today</span>
                  <strong>${formatCurrencyDisplay(totalPaidTodayWithFee)}</strong>
                </Typography>
                {s.remainingBalance > 0 && (
                  <>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <span style={{ color: '#4E4E4E' }}>Remaining balance</span>
                      <strong>${formatCurrencyDisplay(s.remainingBalance)}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Please Note: The displayed total may change if additional charges are incurred before the charge date.
                    </Typography>
                  </>
                )}
              </>
            </CardContent>
          </Card>

          {/* Your registrations card */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AssignmentIcon fontSize="small" /> Your registrations
              </Typography>
              {s.registrations.map((reg, idx) => (
                <Box
                  key={idx}
                  sx={{
                    py: 1,
                    borderBottom: idx < s.registrations.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {reg.eventName}
                    {reg.childName ? ` · ${reg.childName}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dayjs(reg.eventStartDate).format('MM/DD/YYYY')} – {dayjs(reg.eventEndDate).format('MM/DD/YYYY')}
                  </Typography>
                  {(s.couponDiscountTotal ?? 0) > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      Coupon and paid-in-full discounts are shown in the Payment summary above.
                    </Typography>
                  )}
                  {reg.autoPaymentDate && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      Remaining balance of ${formatCurrencyDisplay(getRegistrationRemainingBalance(reg))} auto-charges on{' '}
                      {dayjs(reg.autoPaymentDate).format('MM/DD/YYYY')}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleAddToCalendar(reg)}
                    disabled={scheduleLoading || !isScheduleItemValid(getScheduleForRegistration(reg))}
                    sx={{ mt: 0.5, textTransform: 'none', px: 0 }}
                  >
                    Add to Calendar
                  </Button>
                </Box>
              ))}
            </CardContent>
          </Card>

          {scheduleError && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
              {scheduleError}
            </Typography>
          )}

          {/* Outstanding Forms Section */}
          <Collapse in={showForms}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AssignmentIcon fontSize="small" /> Required Forms
                </Typography>
                
                {formsLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                )}

                {formsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formsError}
                  </Alert>
                )}

                {!formsLoading && !formsError && outstandingForms.length === 0 && (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      All required forms are complete!
                    </Typography>
                  </Box>
                )}

                {!formsLoading && outstandingForms.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {outstandingForms.map((form) => {
                      const rowKey = getFormRowKey(form);
                      const isExpanded = expandedFormRowKeys.has(rowKey);
                      const isLoading = loadingFormId === form.id;
                      const isSubmitting = submittingFormRowKey === rowKey;
                      const isSuccess = submitSuccess.get(rowKey);
                      const formSchema = formSchemas.get(form.id);
                      const formDefinition = formSchema ? parseFormSchema(formSchema.formSchema) : null;

                      return (
                        <Card
                          key={rowKey}
                          variant="outlined"
                          sx={{
                            borderLeft: isSuccess ? '4px solid #4caf50' : '4px solid #88B73E',
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {form.formName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {form.eventName}
                                  {form.childName ? ` · ${form.childName}` : ''}
                                </Typography>
                                {form.dueDate && (
                                  <Typography variant="caption" color="text.secondary">
                                    Due: {dayjs(form.dueDate).format('MM/DD/YYYY')}
                                  </Typography>
                                )}
                              </Box>
                              {isSuccess ? (
                                <CheckCircleIcon sx={{ color: 'success.main' }} />
                              ) : (
                                <Button
                                  size="small"
                                  variant={isExpanded ? 'text' : 'outlined'}
                                  onClick={() => handleFormExpand(form)}
                                  disabled={isLoading || isSubmitting}
                                  sx={{ textTransform: 'none' }}
                                >
                                  {isExpanded ? 'Close' : 'Open form'}
                                </Button>
                              )}
                            </Box>

                            <Collapse in={isExpanded}>
                              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                {isLoading && (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                )}
                                
                                {!isLoading && formDefinition && (
                                  <FormioProvider Formio={Formio} baseUrl="https://form.io" projectUrl="https://form.io">
                                    <Form
                                      src={formDefinition as any}
                                      onFormReady={(instance) => {
                                        formRefs.current.set(rowKey, instance as { submit: () => void });
                                      }}
                                      onSubmit={(submission) => handleFormSubmit(form, submission)}
                                      onError={(err) => console.error('Form error:', err)}
                                      options={{ readOnly: isSubmitting }}
                                    />
                                    <Button
                                      variant="contained"
                                      onClick={() => formRefs.current.get(rowKey)?.submit?.()}
                                      disabled={isSubmitting}
                                      sx={{ mt: 2, textTransform: 'none' }}
                                    >
                                      Submit
                                    </Button>
                                  </FormioProvider>
                                )}
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Collapse>

          {/* What's next card */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <NextPlanIcon fontSize="small" /> What's next
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="medium"
                  endIcon={<PersonSearchIcon />}
                  onClick={() => navigate('/profile')}
                  sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                >
                  View my registrations
                </Button>
                {s.orderType === OrderPaymentType.DEPOSIT && s.registrations.some((r) => r.autoPaymentDate) && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                      Your remaining balance will be charged automatically on the following dates:
                    </Typography>
                    {s.registrations
                      .filter((r) => r.autoPaymentDate)
                      .map((r, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          {r.eventName}
                          {r.childName ? ` · ${r.childName}` : ''}:{' '}
                          <strong>{dayjs(r.autoPaymentDate).format('MMMM D, YYYY')}</strong>
                        </Typography>
                      ))}
                  </Box>
                )}
                {hasOutstandingForms && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Some events require forms before attendance.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="medium"
                      endIcon={showForms ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={handleCompleteFormsClick}
                      sx={{ textTransform: 'none' }}
                    >
                      {showForms ? 'Hide forms' : 'Complete your forms'}
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Typography
          sx={{ mt: 2, backgroundColor: '#fff', padding: 1, borderRadius: 1, border: '1px solid #ccc' }}
          color="#666666"
          fontSize={15}
          textAlign="center"
        >
          Powered by{' '}
          <a href="https://www.kindertales.com/" style={{ textDecoration: 'underline' }}>
            Kindertales
          </a>
        </Typography>
      </Box>
    </div>
  );
};

export default ThankYouPage;
